import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from './components/LoginForm';
import { LocationSelector } from './components/LocationSelector';
import { useAuth } from '@/context/AuthContext';

// Mapeo de rutas a módulos de permisos (debe coincidir con Sidebar.tsx y RootRedirect.tsx)
const routePermissionMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/pos': 'pos',
  '/cash-register': 'cash_register',
  '/void-sales': 'void_sales',
  '/customers': 'customers',
  '/promotions': 'promotions',
  '/products': 'products',
  '/ingredients': 'ingredients',
  '/locations': 'locations',
  '/employees': 'employees',
  '/suppliers': 'suppliers',
  '/stock-config': 'stock_config',
  '/system-parameters': 'system_parameters',
  '/purchase-orders': 'purchase_orders',
  '/distribution': 'distribution',
  '/inventory': 'inventory',
  '/kardex': 'kardex',
  '/transformations': 'transformations',
  '/accounts-payable': 'accounts_payable',
  '/merchandise-movements': 'merchandise_movements',
  '/bread-transfers': 'bread_transfers',
  '/vouchers/manage': 'vouchers',
  '/reports': 'reports'
};

// Orden de prioridad de rutas
const routePriority = [
  '/dashboard',
  '/pos',
  '/cash-register',
  '/void-sales',
  '/customers',
  '/promotions',
  '/products',
  '/ingredients',
  '/locations',
  '/employees',
  '/suppliers',
  '/stock-config',
  '/system-parameters',
  '/purchase-orders',
  '/distribution',
  '/inventory',
  '/kardex',
  '/transformations',
  '/accounts-payable',
  '/merchandise-movements',
  '/bread-transfers',
  '/vouchers/manage',
  '/reports'
];

// Función para obtener la primera ruta accesible
function getFirstAccessibleRoute(permisos: any[] | undefined): string {
  if (!permisos || !Array.isArray(permisos) || permisos.length === 0) {
    return '/dashboard';
  }

  for (const route of routePriority) {
    const requiredModule = routePermissionMap[route];
    if (!requiredModule) {
      return route;
    }

    const hasPermission = permisos.some((permiso: any) => permiso.module === requiredModule);
    if (hasPermission) {
      return route;
    }
  }

  return '/dashboard';
}

export const LoginPage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  // Calcular la primera ruta accesible basada en permisos
  const firstAccessibleRoute = useMemo(() => {
    return getFirstAccessibleRoute(auth.user?.permisos);
  }, [auth.user?.permisos]);

  // Debug: Solo loggear cuando cambien estados importantes
  useEffect(() => {
    console.log('LoginPage state change:', {
      isAuthenticated: auth.isAuthenticated,
      hasUser: !!auth.user,
      hasSelectedLocation: !!auth.selectedLocation,
      locationsCount: auth.locations.length,
      isLoading: auth.isLoading
    });
  }, [auth.isAuthenticated, auth.user, auth.selectedLocation, auth.locations.length, auth.isLoading]);

  // Redirección cuando el usuario está completamente autenticado
  useEffect(() => {
    if (auth.isAuthenticated && auth.selectedLocation) {
      console.log('🚀 Starting redirect to', firstAccessibleRoute, 'in 2 seconds...');

      // Esperar un momento para mostrar el mensaje de bienvenida
      const timer = setTimeout(() => {
        console.log('🚀 Navigating to', firstAccessibleRoute, 'now');
        navigate(firstAccessibleRoute, { replace: true });
      }, 2000); // 2 segundos de delay

      return () => clearTimeout(timer);
    }
  }, [auth.isAuthenticated, auth.selectedLocation, navigate, firstAccessibleRoute]);

  const handleLogin = async (username: string, password: string) => {
    try {
      await auth.login(username, password);
    } catch (error) {
      // El error ya está manejado en el contexto
      console.log('Error en login:', error);
    }
  };


  // Mostrar loading durante autenticación inicial
  if (auth.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary mx-auto"></div>
          <p className="mt-2 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Usuario autenticado y ubicación seleccionada - redirigir o mostrar dashboard
  if (auth.isAuthenticated && auth.selectedLocation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-macartur-secondary rounded-full flex items-center justify-center mb-4">
            <span className="text-white text-2xl">✓</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">¡Bienvenido!</h2>
          <p className="text-macartur-accent1">
            Hola {auth.user?.nombre} {auth.user?.apellidos}{auth.user?.positionId === 3 ? `, trabajando en ${auth.selectedLocation.name}` : ''}
          </p>
          <p className="text-sm text-gray-500 mt-2">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  // Auto-select location para roles que no son CAJERO, priorizando local PRINCIPAL
  const isCajero = auth.user?.rol === 'CAJERO';
  if (auth.isAuthenticated && !auth.selectedLocation && auth.locations.length > 0 && !isCajero) {
    const principalLocation = auth.locations.find((loc: any) => loc.tipoLocal === 'PRINCIPAL');
    const locationToSelect = principalLocation || auth.locations[0];
    console.log('🔧 No es cajero, auto-selecting local:', locationToSelect.name, principalLocation ? '(PRINCIPAL)' : '(fallback)');
    auth.selectLocation(locationToSelect);
  }

  // Cajero autenticado con múltiples locales - mostrar selector
  if (auth.isAuthenticated && !auth.selectedLocation && isCajero && auth.locations.length > 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-4">
            <p className="text-gray-600">
              Hola <span className="font-semibold">{auth.user?.nombre} {auth.user?.apellidos}</span>
            </p>
          </div>
          <LocationSelector
            locations={auth.locations}
            selectedLocation={auth.selectedLocation}
            onLocationSelect={(location) => {
              if (location) {
                auth.selectLocation(location);
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Usuario no autenticado - mostrar formulario de login
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <LoginForm
        onLogin={handleLogin}
        isLoading={auth.isLoading}
        error={auth.error}
      />
    </div>
  );
};